import React, {useCallback, useContext, useMemo, useState} from "react";
import {
	Avatar,
	Chip,
	IconButton,
	Skeleton,
	Tooltip,
	Typography
} from "@mui/material";
import clsx from "clsx";
import moment from "moment";
import BlockTwoToneIcon from "@mui/icons-material/BlockTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import ForwardTwoToneIcon from "@mui/icons-material/ForwardTwoTone";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import LocalOfferTwoToneIcon from "@mui/icons-material/LocalOfferTwoTone";
import MailTwoToneIcon from "@mui/icons-material/MailTwoTone";
import PhoneCallbackTwoToneIcon from "@mui/icons-material/PhoneCallbackTwoTone";
import PlayArrowTwoToneIcon from "@mui/icons-material/PlayArrowTwoTone";
import PublicTwoToneIcon from "@mui/icons-material/PublicTwoTone";
import ReplyTwoToneIcon from "@mui/icons-material/ReplyTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import {amber, blue, blueGrey, green, lime, purple, red} from "@mui/material/colors";
import {AppContext} from "../../../../App";
import {setOpenSnackbar} from "../../../../services/AppServices";
import {getArrayTicketHistory} from "../../../../services/TicketServices";
import {capitalizeWords} from "../../../../utils/GenericUtils";
import {spAslCode} from "../../../../utils/dictionaries/Generics";
import {getDataLabel} from "../../../../utils/TableUtils";
import {TicketStyle} from "../../../../styles/pages/tickets/TicketStyle";
import StoricoTicket from "../storico/StoricoTicket";
import SimpleDialog from "../../../widgets/dialogs/SimpleDialog";

const knownTags = [
	"#RICHIESTA_CHIAMATA",
	"#INOLTRATO",
	"#MOSTRA_USER",
	"#IN_ATTESA_DI_RISPOSTA",
	"#BLOCCA_RISPOSTE",
];

function getSrc(src, type) {
	switch (src) {
		case "CIT":
			return type === "CCM" ? "Callcenter" : "Ticket form";
		default:
			return type === "INT" ? "Interno" : src;
	}
}

function getColor(priority) {
	switch (priority) {
		case "Bassa":
			return green[500];
		case "Media":
			return amber[600];
		case "Alta":
			return red[500];
		case "Urgente":
			return purple[500];
		default:
			return blue[500];
	}
}

const stopClick = (event) => {
	event.preventDefault();
	event.stopPropagation();
};

const writeClipboard = async (value) => {
	const text = String(value);
	if (typeof window !== "undefined" && window.navigator?.clipboard?.writeText) {
		await window.navigator.clipboard.writeText(text);
		return;
	}

	if (typeof document === "undefined") {
		throw new Error("Clipboard unavailable");
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.top = "-9999px";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.focus();
	textarea.select();
	textarea.setSelectionRange(0, text.length);
	const copied = document.execCommand("copy");
	document.body.removeChild(textarea);
	if (!copied) {
		throw new Error("Clipboard copy failed");
	}
};

const TicketCard = ({ticket, setValue = () => {}, handleTicketSelect, showStorico = false}) => {
	const ticketClasses = TicketStyle();
	const appContext = useContext(AppContext);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [storico, setStorico] = useState([]);
	const [loadingHistory, setLoadingHistory] = useState(false);
	const [historyLoaded, setHistoryLoaded] = useState(false);

	const requestText = ticket.dettaglioRichiesta || "";
	const readableRequest = requestText.replaceAll(/#(\S*)/g, "").replaceAll("#", "").trim();
	const isCallRequest = requestText.includes("#RICHIESTA_CHIAMATA");
	const isUserReply = Boolean(ticket.rispostaUtente);
	const documentValue = ticket.codF || ticket.documento || "";
	const priority = ticket.priority || "Normale";
	const createdAt = ticket.dataCreazione ? moment(ticket.dataCreazione).format("DD/MM/YYYY HH:mm") : "Data non disponibile";
	const lastUpdate = ticket.dataUltimaModifica ? getDataLabel(ticket.dataUltimaModifica) : "mai";
	const destinationLabel = spAslCode[ticket.dst] || ticket.dst || "Destinazione non specificata";

	const visibleTags = useMemo(
		() => requestText
			.match(/#(\S*)/g)
			?.filter((tag) => !knownTags.includes(tag))
			|| [],
		[requestText]
	);

	const subjectLabel = useMemo(() => {
		if (ticket.soggetto?.codF === "UTENTE_ANONIMO") {
			return "Utente anonimo";
		}
		const name = `${capitalizeWords(ticket.soggetto?.nome)} ${capitalizeWords(ticket.soggetto?.cognome)}`.trim();
		const documentLabel = ticket.tipoDocumento && documentValue ? `${ticket.tipoDocumento}: ${documentValue}` : documentValue;
		return [documentLabel, name].filter(Boolean).join(" - ");
	}, [documentValue, ticket.soggetto?.codF, ticket.soggetto?.cognome, ticket.soggetto?.nome, ticket.tipoDocumento]);

	const copyToClipboard = useCallback((event, value, message) => {
		stopClick(event);
		if (!value) return;
		writeClipboard(value)
			.then(() => {
				setOpenSnackbar("info", message, appContext.dispatch);
			})
			.catch(() => {
				setOpenSnackbar("warning", "Non riesco a copiare automaticamente questo valore", appContext.dispatch);
			});
	}, [appContext.dispatch]);

	const openHistory = useCallback((event) => {
		stopClick(event);
		setHistoryOpen(true);
		if (historyLoaded || loadingHistory) return;

		setLoadingHistory(true);
		getArrayTicketHistory(ticket.id, (ticketHistory) => {
			setStorico(ticketHistory);
			setHistoryLoaded(true);
		}).finally(() => {
			setLoadingHistory(false);
		});
	}, [historyLoaded, loadingHistory, ticket.id]);

	const handleTagClick = useCallback((event, tag) => {
		stopClick(event);
		setValue(tag.replace(" ", ""));
	}, [setValue]);

	const handleCopyKeyDown = useCallback((event, value, message) => {
		if (event.key === "Enter" || event.key === " ") {
			copyToClipboard(event, value, message);
		}
	}, [copyToClipboard]);

	const handleTakeCharge = useCallback((event) => {
		stopClick(event);
		handleTicketSelect(ticket);
	}, [handleTicketSelect, ticket]);

	return (
		<React.Fragment>
			<div
				className={clsx(ticketClasses.ticketCardInner, {
					[ticketClasses.ticketCardHighlightUser]: isUserReply,
					[ticketClasses.ticketCardHighlightCall]: isCallRequest,
				})}
			>
				<div className={ticketClasses.ticketCardAction}>
					<div className={ticketClasses.ticketCardContent}>
						<div className={ticketClasses.ticketCardHeader}>
							<Avatar className={ticketClasses.ticketAvatar}>
								{ticket.categoria?.charAt(0)?.toUpperCase() || "T"}
							</Avatar>
							<div className={ticketClasses.ticketMainInfo}>
								<Typography component="h3" className={ticketClasses.ticketCardTitle}>
									{ticket.categoria || "Senza categoria"}{ticket.sottoCategoria ? ` / ${ticket.sottoCategoria}` : ""}
								</Typography>
								<Typography className={ticketClasses.ticketCardSubtitle}>
									{getSrc(ticket.src, ticket.type) || "Sorgente non indicata"} - aperto {createdAt} - modificato {lastUpdate} da {ticket.lastModify || "operatore non indicato"}
								</Typography>
							</div>
							<div className={ticketClasses.ticketMeta}>
								<Chip
									size="small"
									label={priority}
									className={ticketClasses.priorityChip}
									style={{backgroundColor: getColor(priority)}}
								/>
							</div>
						</div>

						<div className={ticketClasses.ticketBodyGrid}>
							<div className={ticketClasses.ticketCardBody}>
								<div className={ticketClasses.ticketIdentityGrid}>
									<Tooltip title="Copia ID ticket">
										<span
											className={clsx(ticketClasses.ticketFact, ticketClasses.ticketFactCompact)}
											onClick={(event) => copyToClipboard(event, ticket.id, "ID copiato negli appunti")}
											onKeyDown={(event) => handleCopyKeyDown(event, ticket.id, "ID copiato negli appunti")}
											aria-label={`Copia ID ticket ${ticket.id}`}
											role="button"
											tabIndex={0}
										>
											<span className={ticketClasses.ticketFactLabel}>ID ticket</span>
											<span className={ticketClasses.ticketFactValue}>#{ticket.id}</span>
										</span>
									</Tooltip>
									{subjectLabel && (
										<Tooltip title="Copia documento">
											<span
												className={ticketClasses.ticketFact}
												onClick={(event) => copyToClipboard(event, documentValue, "Documento copiato negli appunti")}
												onKeyDown={(event) => handleCopyKeyDown(event, documentValue, "Documento copiato negli appunti")}
												aria-label={`Copia documento ${documentValue}`}
												role="button"
												tabIndex={0}
											>
												<span className={ticketClasses.ticketFactLabel}>Documento</span>
												<span className={ticketClasses.ticketFactValue}>{subjectLabel}</span>
											</span>
										</Tooltip>
									)}
								</div>

								<div className={clsx(ticketClasses.ticketFact, ticketClasses.ticketMessageBlock)}>
									<span className={ticketClasses.ticketFactLabel}>Messaggio</span>
									<Typography className={ticketClasses.ticketDescription}>
										{readableRequest || "Nessun dettaglio testuale disponibile per questo ticket."}
									</Typography>
								</div>
							</div>

							<IconButton
								className={ticketClasses.ticketPlayButton}
								onClick={handleTakeCharge}
								aria-label="Prendi in carico ticket"
							>
								<PlayArrowTwoToneIcon/>
							</IconButton>
						</div>
					</div>
				</div>

				<div className={ticketClasses.ticketFooter}>
					<div className={ticketClasses.ticketTags}>
						<Chip
							size="small"
							label={destinationLabel}
							className={ticketClasses.ticketTag}
							icon={ticket.dst === "CIT" ? <PublicTwoToneIcon/> : <BusinessTwoToneIcon/>}
							style={{
								"--ticket-chip-color": ticket.dst === "CIT" ? lime[700] : blueGrey[600],
							}}
						/>
						{requestText.includes("#MOSTRA_USER") && (
							<Chip
								size="small"
								label="Visibile"
								className={ticketClasses.ticketTag}
								icon={<VisibilityTwoToneIcon/>}
								style={{"--ticket-chip-color": green[500]}}
							/>
						)}
						{isUserReply && (
							<Chip
								size="small"
								label="Risposta utente"
								className={ticketClasses.ticketTag}
								icon={<ReplyTwoToneIcon/>}
								style={{"--ticket-chip-color": amber[700]}}
							/>
						)}
						{ticket.contattoMail && (
							<Chip
								size="small"
								label="Mail"
								className={ticketClasses.ticketTag}
								icon={<MailTwoToneIcon/>}
								style={{"--ticket-chip-color": green[600]}}
							/>
						)}
						{isCallRequest && (
							<Chip
								size="small"
								label="Chiamata"
								className={ticketClasses.ticketTag}
								icon={<PhoneCallbackTwoToneIcon/>}
								style={{"--ticket-chip-color": red[500]}}
							/>
						)}
						{requestText.includes("#BLOCCA_RISPOSTE") && (
							<Chip
								size="small"
								label="Bloccato"
								className={ticketClasses.ticketTag}
								icon={<BlockTwoToneIcon/>}
								style={{"--ticket-chip-color": red[700]}}
							/>
						)}
						{requestText.includes("#INOLTRATO") && (
							<Chip
								size="small"
								label="Inoltrato"
								className={ticketClasses.ticketTag}
								icon={<ForwardTwoToneIcon/>}
								style={{"--ticket-chip-color": purple[500]}}
							/>
						)}
						{visibleTags.map((item, index) => (
							<Chip
								clickable
								key={item + index}
								size="small"
								label={item.substring(1)}
								className={ticketClasses.ticketTag}
								icon={<LocalOfferTwoToneIcon/>}
								onClick={(event) => handleTagClick(event, item)}
								style={{"--ticket-chip-color": blue[500]}}
							/>
						))}
					</div>

					<div className={ticketClasses.ticketQuickActions}>
						{showStorico && (
							<Tooltip title="Apri storico">
								<IconButton
									size="small"
									className={ticketClasses.ticketIconButton}
									onClick={openHistory}
									aria-label="Apri storico ticket"
								>
									<HistoryTwoToneIcon fontSize="small"/>
								</IconButton>
							</Tooltip>
						)}
					</div>
				</div>
			</div>

			{showStorico && (
				<SimpleDialog
					open={historyOpen}
					handleClose={() => setHistoryOpen(false)}
					title={`Storico ticket #${ticket.id}`}
					subtitle={loadingHistory
						? "Caricamento movimenti"
						: `${storico.length} ${storico.length === 1 ? "movimento registrato" : "movimenti registrati"}`}
					maxWidth="md"
					showCloseButton
					contentClassName={ticketClasses.ticketHistoryContent}
					content={loadingHistory ? (
							<div className={ticketClasses.ticketHistoryLoading}>
								<Skeleton variant="rounded" height={92}/>
								<Skeleton variant="rounded" height={132}/>
								<Skeleton variant="rounded" height={112}/>
							</div>
						) : (
							<StoricoTicket
								storicoTicket={storico}
								ticketClasses={ticketClasses}
								variant="timeline"
							/>
						)}
				/>
			)}
		</React.Fragment>
	);
};

export default React.memo(TicketCard);
